import * as React from "react";
import Navigation from "@/components/Navigation";
import { ProductReferenceTable } from "@/components/ProductReferenceTable";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Database, Shield, Truck, FileText } from "lucide-react";
import { Link } from "react-router-dom";
import { SEO } from "@/components/SEO";

export default function ProductReference() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-secondary via-background to-muted">
      <SEO
        title="AVT Product Reference System | Internal Tool"
        description="Internal reference sheet cross-referencing AVT product names, internal SKUs, logistics codes and factory models."
        noindex
      />
      <Navigation />
      
      <div className="pt-24 pb-12 px-6 lg:px-12">
        <div className="container mx-auto max-w-7xl">
          {/* Header */}
          <div className="mb-8">
            <Button variant="ghost" asChild className="mb-4">
              <Link to="/" className="flex items-center gap-2">
                <ArrowLeft className="w-4 h-4" />
                Back to Home
              </Link>
            </Button>
            
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-6">
              <div>
                <h1 className="font-playfair text-4xl font-light text-primary mb-3">
                  AVT Product Reference System
                </h1>
                <p className="text-muted-foreground max-w-2xl">
                  Master reference sheet for all AVT smart toilet products. Use this system to cross-reference 
                  AVT marketing names with factory models and logistics codes for accurate shipping and customer service.
                </p>
              </div>
              
              <Badge variant="outline" className="text-xs border-primary/20 text-primary bg-primary/5">
                Internal Administrative Tool
              </Badge>
            </div>

            {/* Quick Guide Cards */}
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              <Card className="border-accent/20 bg-accent/5">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Database className="w-4 h-4 text-accent" />
                    AVT Names
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <CardDescription className="text-xs">
                    Customer-facing product names (LUXA, NOVA, ARIA, etc.) for marketing and sales
                  </CardDescription>
                </CardContent>
              </Card>

              <Card className="border-green-200/50 bg-green-50/50">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Shield className="w-4 h-4 text-green-600" />
                    Internal Codes
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <CardDescription className="text-xs">
                    Your internal SKU system (AVT-LUX-001) for inventory and order management
                  </CardDescription>
                </CardContent>
              </Card>

              <Card className="border-orange-200/50 bg-orange-50/50">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Truck className="w-4 h-4 text-orange-600" />
                    Logistics Codes
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <CardDescription className="text-xs">
                    Shipping and warehouse codes (SHIP-LUX-ST) for fulfillment and tracking
                  </CardDescription>
                </CardContent>
              </Card>

              <Card className="border-purple-200/50 bg-purple-50/50">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <FileText className="w-4 h-4 text-purple-600" />
                    Factory Models
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <CardDescription className="text-xs">
                    Original manufacturer model numbers for technical support and parts
                  </CardDescription>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Product Reference Table */}
          <ProductReferenceTable 
            showInternalCodes={true}
            allowExport={true}
          />

          {/* Usage Instructions */}
          <Card className="mt-8 border-amber-200/50 bg-amber-50/20">
            <CardHeader>
              <CardTitle className="text-lg">How to Use This Reference System</CardTitle>
              <CardDescription>
                Guidelines for using the product reference system in daily operations
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-medium mb-2 text-primary">For Customer Service:</h4>
                  <ul className="text-sm text-muted-foreground space-y-1">
                    <li>• When a customer mentions "LUXA," reference internal code AVT-LUX-001</li>
                    <li>• Use logistics codes for shipping inquiries and tracking</li>
                    <li>• Cross-reference with factory models for technical support</li>
                    <li>• Always use AVT product names in customer communications</li>
                  </ul>
                </div>
                
                <div>
                  <h4 className="font-medium mb-2 text-primary">For Inventory & Shipping:</h4>
                  <ul className="text-sm text-muted-foreground space-y-1">
                    <li>• Use internal codes (AVT-XXX-###) for inventory management</li>
                    <li>• Use logistics codes (SHIP-XXX-ST) for warehouse operations</li>
                    <li>• Export CSV for integration with external systems</li>
                    <li>• Reference factory models when ordering parts or replacements</li>
                  </ul>
                </div>
              </div>

              <div className="pt-4 border-t border-amber-200/30">
                <p className="text-sm text-muted-foreground">
                  <strong>Note:</strong> This reference system is automatically integrated into product pages. 
                  Each product page includes metadata with internal codes that can be toggled on/off for customer vs. internal views.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}